import { createBrowserRouter, Navigate } from 'react-router-dom';
import { KioskLock } from '@/screens/KioskLock';
import { ManagerSignIn } from '@/screens/ManagerSignIn';
import { Dashboard } from '@/screens/Dashboard';
import { AdminDashboardLayouts } from '@/screens/AdminDashboardLayouts';
import { AdminUsers } from '@/screens/AdminUsers';
import { AdminUserDetail } from '@/screens/AdminUserDetail';
import { AdminLocations } from '@/screens/AdminLocations';
import { AdminLocationDetail } from '@/screens/AdminLocationDetail';
import { Hr } from '@/screens/Hr';
import { HrTicketDetail } from '@/screens/HrTicketDetail';
import { Tasks } from '@/screens/Tasks';
import { CashRequests } from '@/screens/CashRequests';
import { InventoryRequests } from '@/screens/InventoryRequests';
import { Incidents } from '@/screens/Incidents';
import { Announcements } from '@/screens/Announcements';
import { Documents } from '@/screens/Documents';
import { Reports } from '@/screens/Reports';
import { AdminAuditLog } from '@/screens/AdminAuditLog';
import { AdminHealth } from '@/screens/AdminHealth';
import { KioskUsername } from '@/screens/kiosk/KioskUsername';
import { KioskPin } from '@/screens/kiosk/KioskPin';
import { PinChange } from '@/screens/kiosk/PinChange';
import { RequireAuth } from '@/lib/auth/RequireAuth';
import { RequireAnyGroup } from '@/lib/auth/RequireAnyGroup';
import { RequirePredicate } from '@/lib/auth/RequirePredicate';
import { canManageBaristaUsers, isHrStaff } from '@/lib/auth/rbac';
import { RootLayout } from '@/routes/RootLayout';
import { Tier1Layout } from '@/routes/Tier1Layout';

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <Navigate to="/lock" replace /> },
      { path: '/lock', element: <KioskLock /> },
      { path: '/signin', element: <ManagerSignIn /> },
      { path: '/kiosk/username', element: <KioskUsername /> },
      { path: '/kiosk/pin', element: <KioskPin /> },
      {
        path: '/pin-change',
        element: (
          <RequireAuth>
            <PinChange />
          </RequireAuth>
        ),
      },
      // Authenticated Tier-1 (manager) pages share the persistent global nav,
      // status line, and sign-out via Tier1Layout. The kiosk/Tier-2 routes above
      // deliberately stay outside this layout.
      {
        element: (
          <RequireAuth>
            <Tier1Layout />
          </RequireAuth>
        ),
        children: [
          { path: '/dashboard', element: <Dashboard /> },
          { path: '/tasks', element: <Tasks /> },
          { path: '/cash-requests', element: <CashRequests /> },
          { path: '/inventory-requests', element: <InventoryRequests /> },
          { path: '/incidents', element: <Incidents /> },
          { path: '/announcements', element: <Announcements /> },
          { path: '/documents', element: <Documents /> },
          { path: '/reports', element: <Reports /> },
          {
            path: '/admin/audit',
            element: (
              <RequireAnyGroup groups={['TMKN-Qargo-Admins', 'TMKN-Qargo-CLevel']}>
                <AdminAuditLog />
              </RequireAnyGroup>
            ),
          },
          {
            path: '/admin/health',
            element: (
              <RequireAnyGroup groups={['TMKN-Qargo-Admins', 'TMKN-Qargo-CLevel']}>
                <AdminHealth />
              </RequireAnyGroup>
            ),
          },
          {
            path: '/admin/dashboard-layouts',
            element: (
              <RequireAnyGroup groups={['TMKN-Qargo-Admins']}>
                <AdminDashboardLayouts />
              </RequireAnyGroup>
            ),
          },
          {
            path: '/admin/users',
            element: (
              <RequirePredicate predicate={canManageBaristaUsers}>
                <AdminUsers />
              </RequirePredicate>
            ),
          },
          {
            path: '/admin/users/:id',
            element: (
              <RequirePredicate predicate={isHrStaff}>
                <AdminUserDetail />
              </RequirePredicate>
            ),
          },
          {
            path: '/admin/hr',
            element: (
              <RequirePredicate predicate={canManageBaristaUsers}>
                <Hr />
              </RequirePredicate>
            ),
          },
          {
            path: '/admin/hr/:id',
            element: (
              <RequirePredicate predicate={canManageBaristaUsers}>
                <HrTicketDetail />
              </RequirePredicate>
            ),
          },
          {
            path: '/admin/locations',
            element: (
              <RequirePredicate predicate={canManageBaristaUsers}>
                <AdminLocations />
              </RequirePredicate>
            ),
          },
          {
            path: '/admin/locations/:id',
            element: (
              <RequirePredicate predicate={canManageBaristaUsers}>
                <AdminLocationDetail />
              </RequirePredicate>
            ),
          },
        ],
      },
      { path: '*', element: <Navigate to="/lock" replace /> },
    ],
  },
]);
